import React, { Fragment, useCallback, useEffect, useState } from "react";
import {
  getPM10,
  getPM2_5,
} from "keplerConfigs/config/homeMap/dataset/metrics/pm";
import GeneralKeplerMapContainer from "containers/kepler-maps/GeneralKeplerMap.container";
import {
  getPointPM2_5Heatmap,
  getPointPM10Heatmap,
} from "keplerConfigs/config/environmentMapConfig/pointsConfig";
import {
  getHeatmapPM10Norm,
  getHeatmapPM2_5Norm,
} from "keplerConfigs/config/environmentMapConfig/heatmapsConfig";
import { Box } from "@mui/material";
import { WiDust } from "react-icons/wi";
import _ from "lodash";
import { Loading } from "components/common";
import { mapStateConfig } from "keplerConfigs/config/mapStateConfig";
import { mapStyleConfig } from "keplerConfigs/config/mapStyleConfig";
import FiltersComponent from "components/filtersMap/Filters.component";
import LegendMetricComponent from "components/kleperMap/legend/LegendMetric.component";
import { propsLegendMetric } from "constants/metrics";
import { useTranslation } from "react-i18next";

const optionsChecked = {
  pm10: true,
  pm2_5: false,
};

const MapPMContainer = ({ data, dataDouble }) => {
  const { t } = useTranslation()
  const [config, setConfig] = useState({});
  const [selected, setSelected] = useState([]);
  const [checked, setChecked] = useState(optionsChecked);
  const [dataHeatmap, setDataHeatmap] = useState({});
  const [loading, setLoading] = useState(false);

  const onChange = useCallback(
    (item) => {
      if (!checked[item.name]) {
        setDataHeatmap({});
        setChecked({
          [item.name]: !checked[item.name],
        });
      }
    },
    [checked]
  );

  const getFieldsTooltip = useCallback((name) => {
    return ([
      {
        name: "ID",
        type: "string",
        format: "",
        analyzerType: "STRING",
      },
      {
        name: "lat",
        type: "real",
        format: "",
        analyzerType: "FLOAT",
      },
      {
        name: "lon",
        type: "real",
        format: "",
        analyzerType: "FLOAT",
      },
      {
        name: 'Cluster',
        format: null,
      },
      {
        name: t("Address"),
        format: null,
      },
      {
        name: name,
        type: "real",
        format: "",
        analyzerType: "real",
      },
      {
        name: 'data_norm',
        type: "real",
        format: "",
        analyzerType: "real",
      },
    ])
  }, [t])

  const createInteractionConfig = useCallback(() => {
    let obj = {};
    const configKeys = [
      { key: "pm10", name: "PM10" },
      { key: "pm2_5", name: "PM2.5" },
    ];

    configKeys.map((item) => {
      obj[`${item.key}`] = [
        {
          name: "ID",
          format: null,
        },
        {
          name: 'Cluster',
          format: null,
        },
        {
          name: t("Address"),
          format: null,
        },
        {
          name: t(item.name),
          format: null,
        },
      ];

      return obj;
    });

    let configInteraction = {
      tooltip: {
        fieldsToShow: obj,
        compareMode: false,
        compareType: "absolute",
        enabled: true,
      },
      brush: {
        size: 0.5,
        enabled: false,
      },
      geocoder: {
        enabled: false,
      },
      coordinate: {
        enabled: false,
      },
    };

    return configInteraction;
  }, [t]);

  useEffect(() => {
    if (data && !_.isEmpty(data) && dataDouble && !_.isEmpty(dataDouble)) {
      if (!_.isEmpty(selected)) {
        switch (selected[0].value) {
          case "pm10":
            const fieldsPM10 = getFieldsTooltip('PM10')
            setDataHeatmap(getPM10(data, fieldsPM10));
            break;
          case "pm2_5":
            const fieldsPM2_5 = getFieldsTooltip('PM2.5')
            setDataHeatmap(getPM2_5(dataDouble, fieldsPM2_5));
            break;
          default:
            return;
        }
        setLoading(false);
      }
    } else {
      setDataHeatmap({});
    }
  }, [data, dataDouble, selected, getFieldsTooltip]);

  useEffect(() => {
    setSelected(options.filter((item) => checked[item.name] === true));
  }, [checked]);

  useEffect(() => {
    if (dataHeatmap) {
      const newConfig = {
        datasets: dataHeatmap,
        options: {
          centerMap: true,
          readOnly: true,
        },
        config: {
          visState: {
            filters: [],
            layers: [
              getPointPM10Heatmap(),
              getHeatmapPM10Norm(),

              getPointPM2_5Heatmap(),
              getHeatmapPM2_5Norm(),
            ],
            interactionConfig: createInteractionConfig(),

            layerBlending: "normal",
            splitMaps: [],
            animationConfig: {
              currentTime: null,
              speed: 1,
            },
          },
          mapState: mapStateConfig,
          mapStyle: mapStyleConfig,
        },
      };

      setConfig(newConfig);
    }
  }, [dataHeatmap, createInteractionConfig]);

  return (
    <Fragment>
      {loading ? (
        <Loading />
      ) : (
        <Box sx={{ position: "relative", height: "100%" }}>
          <GeneralKeplerMapContainer height={400} config={config} zoom={12} />
          <Box sx={{ position: "absolute", top: 10, left: 10 }}>
            {selected && !_.isEmpty(selected) && (
              <Fragment>
                <LegendMetricComponent
                  {...propsLegendMetric[selected[0].value]}
                />
              </Fragment>
            )}
          </Box>

          <Box
            sx={{
              position: "absolute",
              top: 20,
              right: 15,
              marginTop: { xs: 2, sm: 0 },
            }}
          >
            <FiltersComponent
              selected={selected}
              options={options}
              checked={checked}
              onChange={onChange}
            />
          </Box>
        </Box>
      )}
    </Fragment>
  );
};

export default MapPMContainer;

const options = [
  {
    label: "PM 10",
    name: "pm10",
    value: "pm10",
    icon: <WiDust style={{ width: 24, height: 24, color: "white" }} />,
  },
  {
    label: "PM 2.5",
    name: "pm2_5",
    value: "pm2_5",
    icon: <WiDust style={{ width: 24, height: 24, color: "white" }} />,
  },
];
